import {allRouteDefinitions} from 'common/src/modules/routing/routing.config';
import {RouteKey} from 'common/src/modules/routing/routing.enums';
import {AppRouteParams, AppRouteQueryParams} from 'common/src/modules/routing/routing.types';
import {promptToRouteResponseSchema} from '../generalNavigationTool.schemas';
import {PromptToRouteResult} from '../generalNavigationTool.types';
import formatRouteDefs from './formatRouteDefs';
import promptToRouteResultFailure from './promptToRouteResultFailure';
import {Caller, ToolUtils} from '../../../copilot.types';
import appendCustomCallerToCallChain from '../../../utils/creators/appendCustomCallerToCallChain';
import openAIUserMessage from '../../../openAI/utils/openAIUserMessage';
import openAISystemMessage from '../../../openAI/utils/openAISystemMessage';

async function promptToRoute(
  prompt: string,
  toolUtils: ToolUtils,
  callChain: Caller[],
): Promise<PromptToRouteResult> {
  const systemPrompt = `Your job is to translate a prompt into a route definition, path parameter values, and query parameter values.

## Steps:

1. Identify which route definition best matches the prompt's intent based on the available route definitions given below.
2. Resolve ALL **required** path parameter values and query parameter values.
4. Do your best to resolve **optional** path parameter values and query parameter values as well, but if you can't, that is OK, just don't include them in your response.
5. Identify when more information is needed - ONLY in the following cases:
   5.1. The route is unidentifiable.
   5.2. The route has **required** parameters that are not present in the prompt.
   5.3. The parameter value doesn't match the type and/or optional enum values of the parameter in the route definition.

**Important:** NEVER request more information just because an optional parameter is missing.

## Current location:
${window.location.pathname}

## Available Routes definitions:

${formatRouteDefs(allRouteDefinitions)}`;

  const response = await toolUtils.invokeStandaloneLLMCall({
    messages: [openAISystemMessage(systemPrompt), openAIUserMessage(prompt)],
    responseSchema: promptToRouteResponseSchema,
    callChain: appendCustomCallerToCallChain('promptToRoute', callChain),
  });

  if (!response.success) {
    const {reasoning, missingInfo} = response;

    return promptToRouteResultFailure({
      reasoning,
      missingInfo,
    });
  }

  const {routeKey, reasoning, confidence} = response;
  const routeDef = allRouteDefinitions[routeKey as RouteKey];

  if (!routeDef || !routeKey) {
    return promptToRouteResultFailure({
      reasoning: `Route not found: ${routeKey}`,
      missingInfo: `The requested route "${routeKey}" was not found in available routes`,
    });
  }

  // Convert array of {key, value} to Record format
  const pathParamValues: AppRouteParams = {};
  response.pathParamValues.forEach(({key, value}) => {
    pathParamValues[key] = value;
  });

  const queryParamValues: AppRouteQueryParams = {};
  response.queryParamValues.forEach(({key, value}) => {
    queryParamValues[key] = value;
  });

  return {
    success: true,
    reasoning,
    pathParamValues,
    queryParamValues,
    confidence,
    routeKey,
    missingInfo: '',
  };
}

export default promptToRoute;
